import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header, { BreadcrumbItem } from '../components/Header';
import { usePageTracking } from '../hooks/usePageTracking';
import CreateMeetingModal from '../components/CreateMeetingModal';
import styles from '../styles/meetingCreateInfoPage.module.css';

const PROGRAM_NAMES: Record<string, string> = {
  saa: 'SAA',
  aa: 'AA',
  na: 'NA',
  oa: 'OA',
  ga: 'GA',
  da: 'DA',
  coda: 'CoDA',
  ca: 'CA',
};

interface MeetingCreateInfoPageProps {
  user?: any;
  onLogout: () => void;
  onLogin: () => void;
}

const MeetingCreateInfoPage: React.FC<MeetingCreateInfoPageProps> = ({
  user,
  onLogout,
  onLogin,
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const programId = (searchParams.get('program') || 'saa').toLowerCase();
  const programLabel = PROGRAM_NAMES[programId] || programId.toUpperCase();
  const [createModalOpen, setCreateModalOpen] = useState(false);

  usePageTracking('Meeting create info');

  const backPath = `/${programId}?tab=meetings`;

  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Home', path: '/', active: false },
    { label: programLabel, path: `/${programId}`, active: false },
    { label: 'Meetings', path: backPath, active: false },
    { label: 'Create a home page', path: '', active: true },
  ];

  const handleCreateSuccess = (meetingCode: string, shouldNavigate = true) => {
    setCreateModalOpen(false);
    if (shouldNavigate) navigate(`/meetings/${meetingCode}`);
  };

  const handleCreateClick = () => {
    if (!user) {
      onLogin();
      return;
    }
    setCreateModalOpen(true);
  };

  return (
    <div className={styles.page}>
      <Header
        breadcrumbs={breadcrumbs}
        user={user}
        onLogout={onLogout}
        onLogin={onLogin}
      />
      <main className={styles.main}>
        <h1 className={styles.title}>
          Meeting/Workshop Home Page
        </h1>

        <div className={styles.content}>
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>What a home page provides</h2>
            <p>
              A home page for your meeting or workshop is a landing page for meeting members and other recovering addicts to find:
            </p>
            <ul>
              <li>Basic meeting information</li>
              <li>Announcements</li>
              <li>Calendar (who is sharing when)</li>
              <li>Readings list</li>
              <li>Phone list (opt-in; members only)</li>
              <li>Secretary&apos;s script (for person leading the meeting)</li>
              <li>Service positions (members only)</li>
              <li>Business meeting notes (members only)</li>
              <li>and more</li>
            </ul>
            <p>
              Your home page will appear and be searchable on your program&apos;s Meetings &amp; Workshops tab on Recovery Commons.
            </p>
          </section>

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.primaryCta}
              onClick={handleCreateClick}
            >
              Create a meeting or workshop home page
            </button>
            <a href={backPath} className={styles.backLink}>
              Back to {programLabel} meetings
            </a>
          </div>
        </div>
      </main>

      {createModalOpen && user && (
        <CreateMeetingModal
          onClose={() => setCreateModalOpen(false)}
          onSuccess={handleCreateSuccess}
        />
      )}
    </div>
  );
};

export default MeetingCreateInfoPage;
